import { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field } from "@/components/ui/form-section";
import { DescriptionList } from "@/components/ui/description-list";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { MultiSelect, type MultiSelectOption } from "@/components/ui/multi-select";
import { CreateAorModal } from "@/components/ui/create-aor-modal";
import { CreateAuthModal } from "@/components/ui/create-auth-modal";
import { CreateIdentifyModal } from "@/components/ui/create-identify-modal";
import { useEndpoints, useCreateEndpoint, useUpdateEndpoint, useDeleteEndpoint } from "@/hooks/use-endpoints";
import { useTransports } from "@/hooks/use-transports";
import { useAuths } from "@/hooks/use-auths";
import { useAors } from "@/hooks/use-aors";
import { useIdentifies } from "@/hooks/use-identifies";
import { useContext, useDeleteContext } from "@/hooks/use-contexts";
import type { Endpoint } from "@/types";
import { CODECS } from "@/types";
import { Plus, ArrowLeft, ExternalLink, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const columns: Column<Endpoint>[] = [
  { key: "name", header: "Name" },
  { key: "dtmf_mode", header: "DTMF" },
  {
    key: "transport_id",
    header: "Transport",
    render: (e) =>
      e.transport_id ? (
        <span className="font-mono text-xs">{e.transport_id.slice(0, 8)}...</span>
      ) : (
        <span className="text-muted-foreground">none</span>
      ),
  },
  {
    key: "auth_ids",
    header: "Auths",
    render: (e) => (
      <span className="text-muted-foreground">
        {(e.auth_ids ?? []).length + (e.outbound_auth_ids ?? []).length}
      </span>
    ),
  },
  {
    key: "aor_ids",
    header: "AORs",
    render: (e) => <span className="text-muted-foreground">{(e.aor_ids ?? []).length}</span>,
  },
  {
    key: "identify_ids",
    header: "Identifies",
    render: (e) => <span className="text-muted-foreground">{(e.identify_ids ?? []).length}</span>,
  },
];

export default function ContextDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ctxId = id ?? "";

  const { data: ctx } = useContext(ctxId);
  const { data: allEndpoints, isLoading, isFetching, error: endpointsError, refetch } = useEndpoints();
  const { data: transports, refetch: refetchTransports } = useTransports();
  const { data: auths, refetch: refetchAuths } = useAuths();
  const { data: aors, refetch: refetchAors } = useAors();
  const { data: identifies, refetch: refetchIdentifies } = useIdentifies();
  const createMut = useCreateEndpoint();
  const updateMut = useUpdateEndpoint();
  const deleteMut = useDeleteEndpoint();
  const deleteCtxMut = useDeleteContext();

  const contextEndpoints = useMemo(
    () => (allEndpoints ?? []).filter((ep) => ep.context_id === ctxId),
    [allEndpoints, ctxId]
  );

  const defaultForm = useMemo(
    () => ({
      name: "",
      context_id: ctxId,
      transport_id: "",
      allow: null as string | null,
      disallow: null as string | null,
      dtmf_mode: "rfc4733",
      timers: "yes",
      timers_min_se: 90,
      timers_sess_expires: 1800,
      direct_media: true,
      direct_media_method: "invite",
      force_rport: true,
      rewrite_contact: false,
      rtp_symmetric: false,
      rtp_ipv6: false,
      ice_support: false,
      media_address: null as string | null,
      media_encryption: "no",
      callerid: null as string | null,
      callerid_privacy: "allowed_not_screened",
      from_user: null as string | null,
      from_domain: null as string | null,
      trust_id_inbound: false,
      trust_id_outbound: false,
      send_pai: false,
      send_rpid: false,
      language: null as string | null,
      moh_suggest: "default",
      accountcode: null as string | null,
      mailboxes: null as string | null,
      acl: null as string | null,
      deny: null as string | null,
      permit: null as string | null,
      t38_udptl: false,
      t38_udptl_ec: "none",
      fax_detect: false,
      auth_ids: [] as string[],
      outbound_auth_ids: [] as string[],
      aor_ids: [] as string[],
      identify_ids: [] as string[],
    }),
    [ctxId]
  );

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Endpoint | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAorModal, setShowAorModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showIdentifyModal, setShowIdentifyModal] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingEndpointDelete, setPendingEndpointDelete] = useState<Endpoint | null>(null);

  const filteredEndpoints = useMemo(
    () =>
      contextEndpoints.filter((ep) =>
        ep.name.toLowerCase().includes(query.toLowerCase())
      ),
    [contextEndpoints, query]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setErrors({});
    setOpen(true);
  };

  const openEdit = (ep: Endpoint) => {
    setEditing(ep);
    setErrors({});
    setForm({
      name: ep.name,
      context_id: ep.context_id,
      transport_id: ep.transport_id ?? "",
      allow: ep.allow ?? null,
      disallow: ep.disallow ?? null,
      dtmf_mode: ep.dtmf_mode,
      timers: ep.timers,
      timers_min_se: ep.timers_min_se,
      timers_sess_expires: ep.timers_sess_expires,
      direct_media: ep.direct_media,
      direct_media_method: ep.direct_media_method,
      force_rport: ep.force_rport,
      rewrite_contact: ep.rewrite_contact,
      rtp_symmetric: ep.rtp_symmetric,
      rtp_ipv6: ep.rtp_ipv6,
      ice_support: ep.ice_support,
      media_address: ep.media_address ?? null,
      media_encryption: ep.media_encryption,
      callerid: ep.callerid ?? null,
      callerid_privacy: ep.callerid_privacy,
      from_user: ep.from_user ?? null,
      from_domain: ep.from_domain ?? null,
      trust_id_inbound: ep.trust_id_inbound,
      trust_id_outbound: ep.trust_id_outbound,
      send_pai: ep.send_pai,
      send_rpid: ep.send_rpid,
      language: ep.language ?? null,
      moh_suggest: ep.moh_suggest,
      accountcode: ep.accountcode ?? null,
      mailboxes: ep.mailboxes ?? null,
      acl: ep.acl ?? null,
      deny: ep.deny ?? null,
      permit: ep.permit ?? null,
      t38_udptl: ep.t38_udptl,
      t38_udptl_ec: ep.t38_udptl_ec,
      fax_detect: ep.fax_detect,
      auth_ids: ep.auth_ids ?? [],
      outbound_auth_ids: ep.outbound_auth_ids ?? [],
      aor_ids: ep.aor_ids ?? [],
      identify_ids: ep.identify_ids ?? [],
    });
    setOpen(true);
  };

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.transport_id) errs.transport_id = "Transport is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    if (editing) {
      updateMut.mutate(
        { id: editing.id, data: form as Partial<typeof form> },
        {
          onSuccess: () => {
            setOpen(false);
            toast.success("Endpoint updated");
          },
          onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
        }
      );
    } else {
      createMut.mutate(form as typeof form, {
        onSuccess: () => {
          setOpen(false);
          toast.success("Endpoint created");
        },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    }
  };

  const handleDelete = (ep: Endpoint) => {
    deleteMut.mutate(ep.id, {
      onSuccess: () => {
        toast.success("Endpoint deleted");
        setPendingEndpointDelete(null);
      },
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  };

  const handleDeleteContext = () => {
    if (!ctx) return;
    deleteCtxMut.mutate(ctx.id, {
      onSuccess: () => {
        toast.success("Context deleted");
        navigate("/config/contexts");
      },
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  };

  const updateField = (f: string, v: unknown) => {
    setForm((prev) => ({ ...prev, [f]: v }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[f];
      return next;
    });
  };

  const transportOpts: MultiSelectOption[] =
    transports?.map((t) => ({ value: t.id, label: t.name })) ?? [];

  const authOpts: MultiSelectOption[] =
    auths?.map((a) => ({ value: a.id, label: a.name })) ?? [];

  const aorOpts: MultiSelectOption[] =
    aors?.map((a) => ({ value: a.id, label: a.name })) ?? [];

  const identifyOpts: MultiSelectOption[] = useMemo(() => {
    const epNameById = new Map((allEndpoints ?? []).map((ep) => [ep.id, ep.name]));
    return (identifies ?? []).map((i) => {
      const assigned = i.endpoint_id ? epNameById.get(i.endpoint_id) : null;
      return {
        value: i.id,
        label: `${i.name} (${assigned ? `assigned to ${assigned}` : "unassigned"})`,
      };
    });
  }, [identifies, allEndpoints]);

  const codecOpts: MultiSelectOption[] = CODECS.map((c) => ({ value: c, label: c }));

  const refreshAll = useCallback(() => {
    refetch();
    refetchTransports();
    refetchAuths();
    refetchAors();
    refetchIdentifies();
  }, [refetch, refetchTransports, refetchAuths, refetchAors, refetchIdentifies]);

  if (!ctx) {
    return (
      <div className="space-y-4">
        <div className="text-muted-foreground">Context not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/config/contexts")}
          title="Back to contexts"
        >
          <ArrowLeft size={18} />
        </Button>
        <PageHeader
          className="flex-1"
          title={ctx.name}
          description={ctx.description}
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshAll}
                title="Refresh all"
              >
                <RefreshCw size={14} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/config/extensions?context_id=${ctx.id}`)}
              >
                <ExternalLink size={14} /> Show Extensions
              </Button>
              <Button onClick={openCreate} size="sm">
                <Plus size={14} /> Create Endpoint
              </Button>
              <Button variant="destructive" size="sm" onClick={() => setPendingDelete(true)}>
                <Trash2 size={14} />
              </Button>
            </>
          }
        />
      </div>

      <DescriptionList
        items={[
          { label: "Context ID", value: ctx.id, mono: true },
          { label: "Endpoints", value: contextEndpoints.length },
          ...(ctx.include_context
            ? [{ label: "Include context", value: ctx.include_context }]
            : []),
        ]}
      />

      <Toolbar
        search={{
          value: query,
          onChange: setQuery,
          placeholder: "Search endpoints...",
        }}
        count={`${filteredEndpoints.length}${query ? ` of ${contextEndpoints.length}` : ""}`}
        onRefresh={refreshAll}
        refreshing={isFetching}
      />

      <DataTable
        columns={columns}
        data={filteredEndpoints}
        isLoading={isLoading}
        error={endpointsError}
        onRetry={() => refetch()}
        getRowKey={(e) => e.id}
        onEdit={openEdit}
        onDelete={(ep) => setPendingEndpointDelete(ep)}
        emptyMessage="No endpoints in this context."
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit Endpoint" : "Create Endpoint"}
        description="PJSIP endpoint configuration for this trunk context."
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={createMut.isPending || updateMut.isPending}
            >
              {editing ? "Update" : "Create"}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          <FormSection title="Identity" columns={2}>
            <Field
              label="Name"
              required
              info="PJSIP endpoint identifier. Used in [endpoint_name] config section. Matched by Identify rules. Example: trunk-provider-a, phone-1001."
              error={errors.name}
            >
              <Input
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                className={errors.name ? "border-destructive" : ""}
              />
            </Field>
            <Field
              label="Transport"
              required
              info="The PJSIP transport this endpoint uses for SIP signaling. Must be a pre-configured Transport. UDP is standard for most SIP trunks. TLS for encrypted signaling."
              error={errors.transport_id}
            >
              <MultiSelect
                options={transportOpts}
                selected={form.transport_id ? [form.transport_id] : []}
                onChange={(vals) => updateField("transport_id", vals[0] ?? "")}
                placeholder="Select transport..."
                onRefresh={() => refetchTransports()}
              />
            </Field>
          </FormSection>

          <FormSection title="Media" columns={1}>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Allowed Media Formats <HelpIcon text="Comma-separated codec allow list. Empty or all means all codecs allowed. Example: ulaw,alaw,g722. Used for codec negotiation in SDP offers." /></Label>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  <RefreshCw size={10} className="inline" /> refresh
                </button>
              </div>
              <MultiSelect
                options={codecOpts}
                selected={form.allow ? form.allow.split(",").filter(Boolean) : []}
                onChange={(vals) => updateField("allow", vals.join(",") || null)}
                placeholder="Select codecs..."
              />
            </div>

            <div className="flex items-center gap-4">
              <Switch
                id="direct_media"
                checked={form.direct_media}
                onChange={(e) => updateField("direct_media", e.target.checked)}
                label={<span>Direct Media <HelpIcon text="When enabled (default yes), Asterisk attempts to send media directly between endpoints after call setup, bypassing the server. Disable if endpoints are behind different NATs." /></span>}
              />
            </div>
          </FormSection>

          <FormSection title="Relationships" columns={1}>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>AORs <HelpIcon text="Address of Record - defines where to reach this endpoint (SIP contact URIs). Multiple AORs means calls are sent to all contacts simultaneously." /></Label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => refetchAors()}
                    className="text-xs text-muted-foreground hover:text-foreground px-1"
                    title="Refresh AORs"
                  >
                    <RefreshCw size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAorModal(true)}
                    className="text-xs text-accent-foreground hover:underline"
                  >
                    <Plus size={12} className="inline" /> New
                  </button>
                </div>
              </div>
              <MultiSelect
                options={aorOpts}
                selected={form.aor_ids}
                onChange={(vals) => updateField("aor_ids", vals)}
                placeholder="Select AORs..."
                onRefresh={() => refetchAors()}
                onCreateNew={() => setShowAorModal(true)}
                createLabel="Create AOR"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Inbound Auth <HelpIcon text="Credentials used to authenticate incoming calls TO this endpoint. If set, Asterisk challenges callers for these credentials. Use for trunks that require auth." /></Label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => refetchAuths()}
                    className="text-xs text-muted-foreground hover:text-foreground px-1"
                    title="Refresh Auths"
                  >
                    <RefreshCw size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAuthModal(true)}
                    className="text-xs text-accent-foreground hover:underline"
                  >
                    <Plus size={12} className="inline" /> New
                  </button>
                </div>
              </div>
              <MultiSelect
                options={authOpts}
                selected={form.auth_ids}
                onChange={(vals) => updateField("auth_ids", vals)}
                placeholder="Select inbound auths..."
                onRefresh={() => refetchAuths()}
                onCreateNew={() => setShowAuthModal(true)}
                createLabel="Create Auth"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Outbound Auth <HelpIcon text="Credentials sent when this endpoint places outbound calls TO a provider. Multiple auths attempted in order if first fails." /></Label>
              <MultiSelect
                options={authOpts}
                selected={form.outbound_auth_ids}
                onChange={(vals) => updateField("outbound_auth_ids", vals)}
                placeholder="Select outbound auths..."
                onRefresh={() => refetchAuths()}
                onCreateNew={() => setShowAuthModal(true)}
                createLabel="Create Auth"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Identify <HelpIcon text="Rules for matching incoming traffic to this endpoint. Matches by source IP/CIDR (e.g., 203.0.113.0/24). Without Identify, endpoint cannot receive calls from unknown sources." /></Label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => refetchIdentifies()}
                    className="text-xs text-muted-foreground hover:text-foreground px-1"
                    title="Refresh Identifies"
                  >
                    <RefreshCw size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowIdentifyModal(true)}
                    className="text-xs text-accent-foreground hover:underline"
                  >
                    <Plus size={12} className="inline" /> New
                  </button>
                </div>
              </div>
              <MultiSelect
                options={identifyOpts}
                selected={form.identify_ids}
                onChange={(vals) => updateField("identify_ids", vals)}
                placeholder="Select identifies..."
                onRefresh={() => refetchIdentifies()}
                onCreateNew={() => setShowIdentifyModal(true)}
                createLabel="Create Identify"
              />
            </div>
          </FormSection>
        </div>
      </Sheet>

      <CreateAorModal
        open={showAorModal}
        onClose={() => setShowAorModal(false)}
        onCreated={(newId) => {
          setForm((prev) => ({ ...prev, aor_ids: [...prev.aor_ids, newId] }));
          refetchAors();
        }}
      />

      <CreateAuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onCreated={(newId) => {
          setForm((prev) => ({ ...prev, auth_ids: [...prev.auth_ids, newId] }));
          refetchAuths();
        }}
      />

      <CreateIdentifyModal
        open={showIdentifyModal}
        onClose={() => setShowIdentifyModal(false)}
        onCreated={(newId) => {
          setForm((prev) => ({ ...prev, identify_ids: [...prev.identify_ids, newId] }));
          refetchIdentifies();
        }}
      />

      <ConfirmDialog
        open={pendingDelete}
        onClose={() => setPendingDelete(false)}
        onConfirm={handleDeleteContext}
        title="Delete Context"
        description={`This will delete the context "${ctx.name}" and detach its endpoints. This action cannot be undone.`}
        loading={deleteCtxMut.isPending}
      />

      <ConfirmDialog
        open={pendingEndpointDelete !== null}
        onClose={() => setPendingEndpointDelete(null)}
        onConfirm={() => {
          if (pendingEndpointDelete) handleDelete(pendingEndpointDelete);
        }}
        title="Delete Endpoint"
        description={`This will delete the endpoint "${pendingEndpointDelete?.name ?? ""}". This action cannot be undone.`}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
